/**
 * Localized country name via Intl.DisplayNames, cached per locale
 * to avoid re-instantiating the (relatively expensive) DisplayNames
 * constructor on every render.
 */

type DisplayNamesCtor = new (
	locales: string | string[],
	options: { type: "region"; fallback?: "code" | "none" },
) => {
	of(code: string): string | undefined;
};

const cache = new Map<string, { of(code: string): string | undefined }>();

function getFormatter(locale: string) {
	// Check runtime availability BEFORE reading the cache so tests that
	// stub out Intl.DisplayNames see the stub, not a previously cached
	// formatter. The cache is an optimization, not an authority.
	const ctor = (Intl as unknown as { DisplayNames?: DisplayNamesCtor }).DisplayNames;
	if (!ctor) return null;

	const existing = cache.get(locale);
	if (existing) return existing;

	try {
		// `fallback: "none"` returns undefined for unknown codes instead of
		// a locale-specific placeholder like "Unknown Region".
		const formatter = new ctor(locale, { type: "region", fallback: "none" });
		cache.set(locale, formatter);
		return formatter;
	} catch {
		return null;
	}
}

export function getCountryDisplayName(countryCode: string, locale: string): string {
	if (!countryCode) return "";

	const normalized = countryCode.toUpperCase();
	const formatter = getFormatter(locale);
	if (!formatter) return normalized;

	// Intl.DisplayNames.of() throws RangeError for structurally-invalid region
	// codes (3-letter alpha, digits, etc.). Cloudflare's CF-IPCountry can pass
	// through values like "T1" (Tor) that aren't valid ISO 3166-1 alpha-2.
	try {
		const name = formatter.of(normalized);
		return name && name !== normalized ? name : normalized;
	} catch {
		return normalized;
	}
}
